/**
 * Shared utility for merging BPG (Benefit Plan Group) data into
 * RFP coverage. Used by both PlanDesigner and PlanEditor to build
 * skeleton benefit-plan entries for plans that exist in BPG but
 * haven't yet arrived via the slower RFP query.
 */

import type { BenefitPlanGroup, RfpCoverage } from "../api/types";

type BenefitPlanEntry = RfpCoverage["benefitPlans"][number];

/**
 * Finds benefit plans present in BPG data but missing from the coverage.
 * Returns skeleton plan entries that can be appended to
 * `coverage.benefitPlans` so the grid's `onCellValueChanged` handler
 * can locate and update them immediately after create / duplicate.
 *
 * The input `existingPlanIds` Set is treated as **read-only**;
 * deduplication of newly discovered plans uses a local copy.
 */
export const findNewPlansFromBpg = (
  benefitPlanGroups: BenefitPlanGroup[] | undefined,
  existingPlanIds: ReadonlySet<string>,
): BenefitPlanEntry[] => {
  if (!benefitPlanGroups) return [];

  const seen = new Set(existingPlanIds);

  return benefitPlanGroups.flatMap((group) =>
    group.benefitPlans
      .filter((bp) => {
        if (seen.has(bp.id)) return false;
        seen.add(bp.id);
        return true;
      })
      .map(
        (bp): BenefitPlanEntry => ({
          id: bp.id,
          identifier: bp.identifier,
          // BPG's PlanOptionNameEnum and BenefitPlanTypeEnum come from
          // @platform/openapi-types while RfpCoverage uses identically-valued
          // but separately-declared enums in rfp.api.types — casts required.
          optionName:
            bp.optionName as unknown as BenefitPlanEntry["optionName"],
          benefits: {},
          type: group.productType as unknown as BenefitPlanEntry["type"],
          // policyPeriod uses the same BaseBenefitPlanPolicyPeriod in both
          // sources, so no cast is needed.
          policyPeriod: group.policyPeriod,
        }),
      ),
  );
};
